import { useFaxStore } from '@/stores/fax';
import { useSettingsForwardStore } from '@/stores/settings/forward';
import { useUsersOperatorStore } from '@/stores/users/operator';
import { type IUserDataModel } from '@/types/user/Types';
import { getBowserName } from '@/utils/os';
import { defineStore } from 'pinia';
import { parseJwtPayload } from '~/types/permission/Types';
// import { useI18n } from "vue-i18n";
import { useFileManagerStore } from '~/stores/fileManager';
import { adminNumber } from '~/types/permission/Types';
import {
  addItemToIndexDB,
  getItemFromIndexDB,
} from '~/utils/helpers/dbManager';

const baseUrl = import.meta.env['VITE_PUSH_BIZ_URL'];

const mothershipDomain = import.meta.env['VITE_BASE_MOTHERSHIP_DOMAIN'];
const title = import.meta.env['VITE_COMPANY_NAME'];

export const useUserStore = defineStore('UserStore', {
  state: () => ({
    user: {
      adminNumber: adminNumber() || '',
      username: '',
      password: '',
      token: '',
      role: '',
      wallet: '0',
    } as IUserDataModel,
    userInfo: {
      ivrs: [] as any[],
      queues: [] as any[],
      timeconditions: [] as any[],
      voicemails: [] as any[],
      faxes: [] as any[],
      numbers: [] as any[],
      diverts: [] as any[],
    },
    operations: [] as any[],
    loading: false,
    userData: null as any,
    avatarUrl: null as string | null,
  }),
  getters: {
    currentUser(state) {
      return state.user;
    },
  },
  actions: {
    async getUserWallet() {
      try {
        const { data, error } = await useFetch<number>('/api/user/wallet', {
          method: 'POST',
          body: {
            number: this.user.adminNumber,
          },
        });
        if (data.value === null) return;
        const credit = Number(data.value.toFixed(0));
        this.user.wallet = credit.toLocaleString();
      } catch (err: any) {
        console.log(err);
      }
    },
    async getInfo() {
      try {
        this.userData = await $fetch<any>('/api/user/info');
      } catch (err: any) {
        console.log(err);
      }
    },
    async getUserInfo() {
      const faxStore = useFaxStore();
      const usersOperatorStore = useUsersOperatorStore();
      const settingsForwardStore = useSettingsForwardStore();
      try {
        this.loading = true;
        await Promise.all([
          faxStore.getFaxesList(),
          usersOperatorStore.getNNavaNumber(),
          settingsForwardStore.getCallForward(),
          this.getInfo(),
        ]);

        if (this.userData) {
          this.userInfo.ivrs = this.userData.ivrs?.ivr_menus;
          this.userInfo.queues = this.userData.queues
            ? this.userData.queues
            : [];
          this.userInfo.timeconditions = this.userData.timecond?.dialplans;
          this.userInfo.voicemails = this.userData.vmails
            ? this.userData.vmails
            : [];
        }
        this.userInfo.faxes = faxStore.faxes || [];
        this.userInfo.numbers = usersOperatorStore.nnavaNumber || [];
        this.userInfo.diverts = settingsForwardStore.callForward || [];
        this.initializeOperations();
        this.loading = false;
      } catch (err: any) {
        this.loading = false;
        console.log(err);
      }
    },
    initializeOperations() {
      // const { t } = useI18n();
      const res: any[] = [];

      if (this.userInfo.numbers.length) {
        const usersOperatorStore = useUsersOperatorStore();
        // res.push({
        //   header: t("numbersOf", {
        //     title,
        //   })
        // });
        res.push({ header: `شماره های ${title}` });
        for (let i = 0; i < this.userInfo.numbers.length; i++) {
          res.push({
            title: usersOperatorStore.getOperatorName(
              this.userInfo.numbers[i].number
            ),
            value: `menu-exec-app:transfer 0990${this.userInfo.numbers[i].number} XML nava-${this.user.adminNumber}${mothershipDomain}`,
            type: 'numbers',
          });
        }
        res.push({ divider: true });
      }

      if (this.userInfo.diverts.length) {
        // res.push({ header: t("divertNumbers") });
        res.push({ header: 'شماره های دایورتی' });
        for (let i = 0; i < this.userInfo.diverts.length; i++) {
          res.push({
            title: this.userInfo.diverts[i].destination_data,
            value: `menu-exec-app:transfer divert-${this.userInfo.diverts[i].destination_data} XML public`,
            type: 'diverts',
          });
        }
        res.push({ divider: true });
      }

      if (this.userInfo.queues?.length) {
        // res.push({ header: t("callCenters") });
        res.push({ header: 'مراکز تماس' });
        for (let i = 0; i < this.userInfo.queues.length; i++) {
          res.push({
            title: this.userInfo.queues[i].Queue_name,
            value: `menu-exec-app:transfer ${this.userInfo.queues[i].Queue_extension} XML nava-${this.user.adminNumber}${mothershipDomain}`,
          });
        }
        res.push({ divider: true });
      }

      if (this.userInfo.ivrs?.length) {
        // res.push({ header: t("ivrMenus") });
        res.push({ header: 'منوهای IVR' });
        for (let i = 0; i < this.userInfo.ivrs.length; i++) {
          res.push({
            title: this.userInfo.ivrs[i].ivr_menu_name,
            value: `menu-exec-app:transfer ${this.userInfo.ivrs[i].ivr_menu_extension} XML nava-${this.user.adminNumber}${mothershipDomain}`,
            type: 'ivr',
          });
        }
        res.push({ divider: true });
      }

      if (this.userInfo.timeconditions?.length) {
        // res.push({ header: t("timeConditions") });
        res.push({ header: 'شرط های زمانی' });
        for (let i = 0; i < this.userInfo.timeconditions.length; i++) {
          if (this.userInfo.timeconditions[i].dialplan_enabled === 'true') {
            res.push({
              title: this.userInfo.timeconditions[i].dialplan_name,
              value: this.handleTimeCondition(
                this.userInfo.timeconditions[i].dialplan_number
              ),
              type: 'timeConditions',
            });
          }
        }
        res.push({ divider: true });
      }

      if (this.userInfo.voicemails?.length) {
        // res.push({ header: t("voiceMail") });
        res.push({ header: 'پیام گیر صوتی' });
        for (let i = 0; i < this.userInfo.voicemails.length; i++) {
          res.push({
            title: this.userInfo.voicemails[i].voicemail_id,
            value: `menu-exec-app:transfer *99${this.userInfo.voicemails[i].voicemail_id} XML nava-${this.user.adminNumber}${mothershipDomain}`,
            type: 'voicemail',
          });
        }
        res.push({ divider: true });
      }

      if (this.userInfo.faxes.length) {
        // res.push({ header: t("fax") });
        res.push({ header: 'فکس' });
        for (let i = 0; i < this.userInfo.faxes.length; i++) {
          res.push({
            title: this.userInfo.faxes[i].FaxName,
            value: `menu-exec-app:transfer ${this.userInfo.faxes[i].FaxExtension} XML nava-${this.user.adminNumber}${mothershipDomain}`,
            type: 'fax',
          });
        }
        res.push({ divider: true });
      }
      res.pop();

      this.operations = res;
    },
    findOperation(key: string) {
      const findItem = this.operations.find((obj) => obj.value == key);
      return findItem ? findItem.value : null;
    },
    handleTimeCondition(text: string) {
      return `menu-exec-app:transfer ${text} XML nava-${this.user.adminNumber}${mothershipDomain}`;
    },
    // updateOperationList(type: string, action: string = 'insert', updatedItem: any) {
    //   if (action == 'insert' && updatedItem.dialplan_enabled == "true") {
    //     const indexToInsert = this.operations.findLastIndex(
    //       (item) => item.type === type
    //     );
    //     const item = {
    //       title: updatedItem.dialplan_name,
    //       value: this.handleTimeCondition(updatedItem.dialplan_number),
    //       type: "timeConditions",
    //     }
    //     this.operations.splice(indexToInsert + 1, 0, item);
    //   }
    // },
    async logOut() {
      const jwt = parseJwtPayload();
      const token = localStorage.getItem('token');
      const platform: any = window.navigator.platform;
      const deviceMemory: any = window.navigator.deviceMemory;
      const myHeaders = new Headers();
      myHeaders.append('Authorization', `Bearer ${token}`);
      myHeaders.append('Content-Type', 'application/json');
      const raw = JSON.stringify({
        user_id: jwt.sub,
        device_id: `${
          jwt.sub
        }-${getBowserName()}-${platform}-${deviceMemory}/cp_dashboard`,
      });
      const requestOptions = {
        method: 'POST',
        headers: myHeaders,
        body: raw,
      };
      await fetch(`${baseUrl}/unsubscribe`, requestOptions)
        .then((response) => {
          const savedLang = localStorage.getItem('language') || 'fa';
          localStorage.clear();
          localStorage.setItem('language', savedLang);
        })
        .catch((err) => {});
    },

    async getUsersInfo(list: string[]) {
      try {
        const data = await $fetch<any>(`/api/user/avatar`, {
          method: 'POST',
          body: {
            uuid: list,
          },
        });
        return data.avatars;
      } catch (err: any) {
        console.log(err);
      }
    },

    async getAvatar(uuid: string) {
      const fileManagerStore = useFileManagerStore();

      try {
        const data = await this.getUsersInfo([uuid]);
        const targetItem = data.find((item) => item.uuid === uuid);
        const avatarId = targetItem?.avatar || '';
        if (!avatarId) return;

        const item = await getItemFromIndexDB('images', avatarId);
        if (item && item.file) {
          this.avatarUrl = URL.createObjectURL(item.file);
        } else {
          const res = await fileManagerStore.downloadFileCellar(
            { type: 'thumbnail', id: avatarId },
            false
          );
          if (!res) return;
          this.avatarUrl = res.url;
          addItemToIndexDB('images', {
            id: avatarId,
            file: res.file,
          });
        }
      } catch (err: any) {
        console.log(err);
      }
    },
  },
});
